import { memo, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiInputSelectBox } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { mdiThumbUpOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import CloseoutIssueThumbnail from "./components/CloseoutIssueThumbnail";
import EditableNoteDetail from "./components/EditableNoteDetail";
import { ISSUE_RESULT_NOTE_TYPES, getNoteHistoryState } from "../issuesUtil";

const NOTE_TYPES = ISSUE_RESULT_NOTE_TYPES;

const getCheckedSteps = (isChecked) => {
  const status = {
    complaint: isChecked,
    diagnosis: isChecked,
    repair: isChecked,
    summary: isChecked,
    verificationRequest: isChecked,
    verificationResponse: isChecked,
  };
  return status;
};

function Subtitle({ text }) {
  return (
    <Box display="flex" gap={1} alignItems="center">
      <Typography fontSize={14} fontWeight={600} color="text.primary">
        {text}
      </Typography>
    </Box>
  );
}

export const RichIssueReviewContent = memo(
  ({
    disabled,
    edit,
    getFields,
    sections,
    issueImages,
    repairImages,
    setShowImagesViewer,
    setImagesInViewer,
    handleAddIssueImage,
    handleAddRepairImage,
    handleAddAssessImage,
    handleAddReviewImage,
    removeIssueImage,
    removeRepairImage,
    removeAssessImage,
    removeReviewImage,
    onShowIssueImageViewer,
    assessImages,
    reviewImages,
    isCheckedStep,
    setIsCheckedStep,
    initialValues = {},
    assignedUserId,
    isTask,
    currentIssue,
  }) => {
    const { classes } = useStyles();

    const getSectionFields = (section) =>
      getFields({ section, disabled }) || [];
    const [checkedStep, setCheckedStep] = useState(() =>
      getCheckedSteps(isCheckedStep)
    );
    const noteHistory = currentIssue?.issueResult?.noteHistory || [];

    useEffect(() => {
      setCheckedStep(
        getCheckedSteps(
          Boolean(currentIssue?.issueResult?.reviewedByDispatcher)
        )
      );
    }, [currentIssue?.id, currentIssue?.issueResult?.reviewedByDispatcher]);

    const createPreviewHandler =
      (documents, onShowViewer) =>
      (event, selectedIndex = 0) => {
        event.stopPropagation();

        if (documents.length > 0) {
          const selectedDocument = documents[selectedIndex];
          const viewerDocuments = selectedDocument
            ? [
                selectedDocument,
                ...documents.filter((_, index) => index !== selectedIndex),
              ]
            : documents;

          setShowImagesViewer(true);
          setImagesInViewer(viewerDocuments);
          onShowViewer();
        }
      };

    const diagnosisFields = getSectionFields(sections.DIAGNOSED_ISSUE).map(
      (field) => ({
        ...field,
        noteType: NOTE_TYPES.DIAGNOSED_ISSUE,
      })
    );
    const repairDetailsFields = getSectionFields(sections.REPAIR_DETAILS);
    const resolutionFields = getSectionFields(sections.RESOLUTION);

    const sectionDictionary = {
      complaint: {
        subtitle: isTask ? "Original Note" : "Complaint",
        editableBox: [
          {
            fieldName: "originalDescription",
            label: isTask ? "Original Note" : "Complaint Description",
            multiline: true,
            minRows: 4,
            maxRows: 10,
            required: !isTask,
            noteType: NOTE_TYPES.ORIGINAL_DESCRIPTION,
            noteHistory: getNoteHistoryState({
              noteHistory,
              noteType: NOTE_TYPES.ORIGINAL_DESCRIPTION,
              assignedUserId,
              isApproved: checkedStep.complaint,
            }),
          },
        ],
        autocompleteFields: [],
        images: {
          documents: assessImages,
          onChange: handleAddAssessImage,
          onDelete: removeAssessImage,
          onPreview: createPreviewHandler(assessImages, onShowIssueImageViewer),
        },
        shouldRender: true,
      },
      diagnosis: {
        subtitle: isTask ? "Camera Line Details" : "Diagnostic",

        autocompleteFields: isTask
          ? [
              ...resolutionFields.map((field) => ({
                ...field,
                containerStyle: { flex: "1 1 0", minWidth: 0 },
              })),
              {
                name: "billable",
                label: "Is This Billable?",
                noteType: NOTE_TYPES.IS_BILLABLE,
                options: [
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ],
                containerStyle: { flex: "0 0 160px" },
                style: { width: "100%" },
              },
            ]
          : diagnosisFields,
        images: {
          documents: issueImages,
          onChange: handleAddIssueImage,
          onDelete: removeIssueImage,
          onPreview: createPreviewHandler(issueImages, onShowIssueImageViewer),
        },
        editableBox: [
          ...(isTask &&
          edit.getValue("repairStatus") === "INCOMPLETE_REPAIR_RETURN"
            ? [
                {
                  fieldName: "incompleteReason",
                  label: "Incomplete Reason",
                  multiline: true,
                  minRows: 2,
                  maxRows: 2,
                  noteHistory: getNoteHistoryState({
                    noteHistory,
                    noteType: NOTE_TYPES.INCOMPLETE_REASON,
                    assignedUserId,
                    isApproved: checkedStep.diagnosis,
                  }),
                },
              ]
            : []),
          {
            fieldName: "issueDescription",
            label: isTask ? "Description" : "Diagnosis Description",
            multiline: true,
            minRows: 4,
            maxRows: 10,
            required: true,
            noteHistory: getNoteHistoryState({
              noteHistory,
              noteType: NOTE_TYPES.DESCRIPTION,
              assignedUserId,
              isApproved: checkedStep.diagnosis,
            }),
          },
        ],
        noteType: NOTE_TYPES.DESCRIPTION,
        shouldRender: true,
      },
      repair: {
        subtitle: "Repair",
        autocompleteFields: [],
        images: {
          documents: repairImages,
          onChange: handleAddRepairImage,
          onDelete: removeRepairImage,
          onPreview: createPreviewHandler(repairImages, onShowIssueImageViewer),
        },
        editableBox: [
          {
            fieldName: repairDetailsFields[0]?.name,
            label: "Repair Details",
            multiline: true,
            minRows: 4,
            maxRows: 10,
            required: true,
            noteHistory: getNoteHistoryState({
              noteHistory,
              noteType: NOTE_TYPES.REPAIR_DESCRIPTION,
              assignedUserId,
              isApproved: checkedStep.repair,
            }),
          },
        ],
        noteType: NOTE_TYPES.REPAIR_DESCRIPTION,
        shouldRender: !isTask,
      },
      summary: {
        subtitle: "Summary",

        autocompleteFields: [
          ...resolutionFields.map((field) => ({
            ...field,
            noteType: "Status",
            containerStyle: { flex: "1 1 0", minWidth: 0 },
          })),
          {
            name: "billable",
            label: "Is This Billable?",
            noteType: NOTE_TYPES.IS_BILLABLE,
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ],
            containerStyle: { flex: "0 0 160px" },
            style: { width: "100%" },
          },
        ],
        images: {
          documents: reviewImages,
          onChange: handleAddReviewImage,
          onDelete: removeReviewImage,
          onPreview: createPreviewHandler(reviewImages, onShowIssueImageViewer),
        },
        editableBox: [
          ...(!isTask &&
          edit.getValue("repairStatus") === "INCOMPLETE_REPAIR_RETURN"
            ? [
                {
                  fieldName: "incompleteReason",
                  label: "Incomplete Reason",

                  noteHistory: getNoteHistoryState({
                    noteHistory,
                    noteType: NOTE_TYPES.INCOMPLETE_REASON,
                    assignedUserId,
                    isApproved: checkedStep.summary,
                  }),
                },
              ]
            : []),
          {
            fieldName: "reviewDescription",
            label: "Summary",
            multiline: true,
            minRows: 4,
            maxRows: 10,
            noteHistory: getNoteHistoryState({
              noteHistory,
              noteType: NOTE_TYPES.REVIEW_DESCRIPTION,
              assignedUserId,
              isApproved: checkedStep.summary,
            }),
          },
        ],
        noteType: NOTE_TYPES.REVIEW_DESCRIPTION,
        shouldRender: !isTask,
      },
      verificationRequest: {
        subtitle: "Verification Request",

        editableBox: [
          {
            fieldName: "verificationRequest",
            label: "Verification Request",
            noteType: NOTE_TYPES.VERIFICATION_REQUEST,
            noteHistory: {
              ...getNoteHistoryState({
                noteHistory,
                noteType: NOTE_TYPES.VERIFICATION_REQUEST,
                assignedUserId,
                isApproved: checkedStep.verificationRequest,
              }),
              state: "default",
            },
            multiline: true,
            minRows: 3,
            maxRows: 4,
            isShowOriginalDisabled: true,
            containerStyle: { flex: "1 1 0", minWidth: 0 },
          },
        ],
        shouldRender: true,
      },
      verificationResponse: {
        subtitle: "Verification Response",
        editableBox: [
          {
            fieldName: "verificationResponse",
            label: "Verification Response",
            noteType: NOTE_TYPES.VERIFICATION_RESPONSE,
            noteHistory: {
              ...getNoteHistoryState({
                noteHistory,
                noteType: NOTE_TYPES.VERIFICATION_RESPONSE,
                assignedUserId,
                isApproved: checkedStep.verificationResponse,
              }),
              state: "default",
            },
            multiline: true,
            minRows: 3,
            maxRows: 4,
            isShowOriginalDisabled: true,
            containerStyle: { flex: "1 1 0", minWidth: 0 },
          },
        ],
        shouldRender: true,
      },
    };

    const requiredSectionKeys = Object.entries(sectionDictionary)
      .filter(([, section]) => section.shouldRender)
      .map(([key]) => key);

    const renderAutocomplete = (field, _, fields, completed, noteHistory) => {
      const containerStyle =
        fields.length === 1
          ? {
              flex: "1 1 0",
              minWidth: 0,
              width: "100%",
              ...field.containerStyle,
            }
          : field.containerStyle;

      const fieldState = getNoteHistoryState({
        noteHistory,
        noteType: field.noteType,
        assignedUserId,
        isApproved: completed,
        hasUnsavedChange:
          Object.prototype.hasOwnProperty.call(initialValues, field.name) &&
          String(edit.getValue(field.name) ?? "") !==
            String(initialValues[field.name] ?? ""),
      });

      const isModified = edit.isDirtyField(field.name);

      fieldState.state = isModified ? "warning" : fieldState.state;

      const stateClass = {
        warning: classes.warningAutocomplete,
        success: completed && classes.completedAutocomplete,
      }[fieldState.state];

      return (
        <Box key={field.name} className={stateClass} sx={containerStyle}>
          <MiInputSelectBox
            name={field.name}
            label={field.label}
            options={field.options}
            disableNone
            value={edit.getValue(field.name) ?? ""}
            sx={{
              "& .MuiInputBase-input": { fontSize: 12 },
              "& .MuiInputLabel-root": { fontSize: 12, top: 2 },
              "& .MuiInputLabel-shrink": {
                fontSize: 16,
              },
              ...field.style,
            }}
            renderOption={(option) => option?.label || option?.value || ""}
            disabled={disabled || field.disabled}
            required={field.required}
            handleChange={(event) =>
              edit.update({ [field.name]: event.target.value })
            }
          />
        </Box>
      );
    };

    const renderImages = (imageConfig) => {
      if (!imageConfig) {
        return null;
      }

      return (
        <Box className={classes.imageColumn}>
          <CloseoutIssueThumbnail
            documents={imageConfig.documents}
            disabled={disabled || imageConfig.readOnly}
            onChange={imageConfig.onChange}
            onPreview={imageConfig.onPreview}
            onDelete={imageConfig.onDelete}
          />
        </Box>
      );
    };

    const renderSection = ([key, section]) => {
      const noteHistory = currentIssue?.issueResult?.noteHistory || [];

      if (!section.shouldRender) {
        return null;
      }

      const isDirty = section.editableBox?.some((box) =>
        edit.isDirtyField(box.fieldName)
      );
      const isChanged = section?.editableBox?.some(
        (box) => box.noteHistory.state === "warning"
      );

      const isDirtyAutoComplete = section.autocompleteFields?.some((field) =>
        edit.isDirtyField(field.name)
      );

      const isChangedAutocomplete = section.autocompleteFields?.some(
        (field) =>
          getNoteHistoryState({
            noteHistory,
            noteType: field.noteType,
            assignedUserId,
            isApproved: checkedStep[key],
          }).state === "warning"
      );

      const isModified =
        isChanged || isChangedAutocomplete || isDirty || isDirtyAutoComplete;

      const isValidSection =
        (section.editableBox
          .filter((box) => box.required)
          .every((box) => edit.allFilled(box.fieldName)) &&
          section.autocompleteFields
            ?.filter((field) => field.required)
            .every((field) => edit.allFilled(field.name))) ||
        key === "verificationRequest" ||
        key === "verificationResponse";

      return (
        <Box
          className={section.images ? classes.splitSection : classes.body}
          key={key}
        >
          <Box className={classes.sectionContent}>
            {section.subtitle && <Subtitle text={section.subtitle} />}
            {section.autocompleteFields?.length > 0 && (
              <Box className={classes.autocompleteRow}>
                {section.autocompleteFields.map((field, index, fields) =>
                  renderAutocomplete(
                    field,
                    index,
                    fields,
                    checkedStep[key],
                    noteHistory
                  )
                )}
              </Box>
            )}
            {section.editableBox &&
              section.editableBox.map((box, index) => {
                return (
                  <EditableNoteDetail
                    key={box.name + index}
                    detailField={{
                      ...box,
                      name: box.fieldName,
                      width: "100%",
                    }}
                    edit={edit}
                    disabled={disabled}
                    completed={checkedStep[key]}
                    historyState={box.noteHistory}
                    isShowOriginalDisabled={box?.isShowOriginalDisabled}
                  />
                );
              })}
            {!disabled && (
              <MiButton
                disabled={!isValidSection}
                title={isModified ? "Edited" : "Validate"}
                variant={
                  checkedStep[key]
                    ? BUTTON_VARIANTS.CONTAINED
                    : BUTTON_VARIANTS.OUTLINED
                }
                color={isModified ? "orange" : "green"}
                onClick={() => {
                  const checkedStatus = {
                    ...checkedStep,
                    [key]: !checkedStep[key],
                  };
                  setCheckedStep(checkedStatus);
                  const allChecked = requiredSectionKeys.every(
                    (sectionKey) => checkedStatus[sectionKey]
                  );
                  setIsCheckedStep(allChecked);
                }}
                className={`${classes.btn} ${
                  isModified && checkedStep[key] ? classes.modifiedBtn : ""
                }`}
                style={{
                  width: 80,
                  padding: 2,
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "flex-start",
                }}
                endIcon={
                  checkedStep[key]
                    ? { path: mdiThumbUpOutline, iconSize: 0.7 }
                    : null
                }
              />
            )}
          </Box>
          {renderImages(section.images)}
        </Box>
      );
    };

    return Object.entries(sectionDictionary).map(renderSection);
  }
);

const useStyles = makeStyles()((theme) => ({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  splitSection: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  sectionContent: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 0,
    marginBottom: theme.spacing(2),
  },
  imageColumn: {
    marginTop: theme.spacing(7),
  },
  autocompleteRow: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    width: "100%",
  },
  completedAutocomplete: {
    backgroundColor: theme.palette.success.light,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.success.main,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.success.dark,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.success.main,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.success.dark,
    },
  },
  warningAutocomplete: {
    borderColor: theme.palette.orange.dark,
    backgroundColor: theme.palette.orange.light,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.dark,
    },
    "& .MuiSelect-outlined.Mui-disabled": {
      borderColor: theme.palette.orange.dark,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.orange.dark,
    },
  },

  modifiedBtn: {
    ":hover": {
      backgroundColor: theme.palette.orange.darkAccent,
      color: theme.palette.common.white,
    },
    color: theme.palette.common.white,
  },
  detailFields: {
    width: "100%",
  },
}));
